<template>
  <div>
    <h1 class="text-2xl font-bold mb-4">Suggestions</h1>
    <p class="mb-6">Manage user suggestions here.</p>

    <div v-if="loading" class="text-center text-gray-500">Loading suggestions...</div>
    <div v-else-if="error" class="text-center text-red-500">{{ error }}</div>
    <div v-else-if="suggestions.length === 0" class="text-center text-gray-500">No suggestions found.</div>

    <div v-else class="overflow-x-auto">
      <table class="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
        <thead>
          <tr class="bg-gray-100">
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Email</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Suggestion</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Status</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Date</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="suggestion in suggestions" :key="suggestion.id" class="border-t border-gray-200 hover:bg-gray-50">
            <td class="px-4 py-2 text-sm text-gray-600">{{ suggestion.user_email }}</td>
            <td class="px-4 py-2 text-sm text-gray-600 truncate max-w-xs" :title="suggestion.suggestion_text">
              {{ suggestion.suggestion_text }}
            </td>
            <td class="px-4 py-2 text-sm">
              <span 
                :class="suggestion.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'"
                class="px-2 py-1 rounded-full text-xs font-semibold"
              >
                {{ suggestion.is_approved ? 'Approved' : 'Pending' }}
              </span>
            </td>
            <td class="px-4 py-2 text-sm text-gray-600">{{ formatDate(suggestion.created_at) }}</td>
            <td class="px-4 py-2 text-sm flex gap-3">
              <button
                v-if="!suggestion.is_approved"
                @click="approveSuggestion(suggestion.id)"
                class="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-md text-xs"
              >
                Approve
              </button>
              <button
                @click="deleteSuggestion(suggestion.id)"
                class="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-md text-xs"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'

const suggestions = ref([])
const loading = ref(false)
const error = ref(null)

const fetchSuggestions = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/suggestions`)
    if (!response.ok) throw new Error('Failed to fetch suggestions')
    const data = await response.json()
    suggestions.value = data
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

const approveSuggestion = async id => {
  const result = await Swal.fire({
    title: 'Approve Suggestion?',
    text: "Are you sure you want to approve this suggestion?",
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#10B981',
    cancelButtonColor: '#d33',
    confirmButtonText: 'Yes, approve it!'
  });

  if (!result.isConfirmed) return;

  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/suggestions/${id}/approve`, {
      method: 'PUT'
    })
    if (!response.ok) throw new Error('Failed to approve suggestion')
    
    // Update local state
    const suggestion = suggestions.value.find(s => s.id === id)
    if (suggestion) suggestion.is_approved = 1
    
    Swal.fire({
      icon: 'success',
      title: 'Approved!',
      text: 'Suggestion approved successfully!',
      confirmButtonColor: '#BF3131'
    });
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: err.message,
      confirmButtonColor: '#BF3131'
    });
  }
}

const deleteSuggestion = async id => {
  const result = await Swal.fire({
    title: 'Delete Suggestion?',
    text: "You won't be able to revert this!",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it!'
  });

  if (!result.isConfirmed) return;

  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/suggestions/${id}`, {
      method: 'DELETE'
    })
    if (!response.ok) throw new Error('Failed to delete suggestion')
    suggestions.value = suggestions.value.filter(s => s.id !== id)
    
    Swal.fire({
      icon: 'success',
      title: 'Deleted!',
      text: 'Suggestion deleted successfully!',
      confirmButtonColor: '#BF3131'
    });
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: err.message,
      confirmButtonColor: '#BF3131'
    });
  }
}

onMounted(fetchSuggestions)
</script>
